import React, { useState, useRef, useEffect } from "react";
import {
  Check,
  X,
  Lightning,
  Buildings,
  StackSimple,
} from "@phosphor-icons/react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Textarea,
  Switch,
} from "@heroui/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  TicketInternalStatus,
  TicketPriority,
  TicketLabel,
} from "shared/types/helpdesk";
import { useBuildingsAndUnits } from "hooks/useBuildingsAndUnits";
import { ChevronDown, ChevronUp } from "assets/icons";
import { useActiveUsers } from "shared/contexts/users.context";

interface QuickAddTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketAdded?: () => void;
}

interface BuildingDropdownProps {
  selectedBuilding: any;
  onBuildingSelect: (building: any) => void;
  isDisabled?: boolean;
}

interface UnitDropdownProps {
  selectedUnit: any;
  selectedBuildingId?: string;
  onUnitSelect: (unit: any) => void;
  onBuildingAutoSelect?: (buildingId: string) => void;
  isDisabled?: boolean;
}

interface LabelDropdownProps {
  selectedLabel: TicketLabel | null;
  onLabelSelect: (label: TicketLabel | null) => void;
  isDisabled?: boolean;
}

const BuildingDropdown: React.FC<BuildingDropdownProps> = ({
  selectedBuilding,
  onBuildingSelect,
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { buildings, isLoading } = useBuildingsAndUnits();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredBuildings = buildings.filter(
    (building) =>
      building.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      building.company?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleBuildingSelect = (building: any) => {
    onBuildingSelect(building);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isDisabled && !isLoading && setIsOpen(!isOpen)}
        disabled={isDisabled || isLoading}
        className={`flex min-h-[40px] w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
          isDisabled || isLoading
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
            : "border-neutral-primary/20 hover:border-moss-primary focus:border-moss-primary bg-white"
        }`}
      >
        {isLoading ? (
          <span className="text-neutral-secondary-mid text-sm">Laden...</span>
        ) : selectedBuilding ? (
          <div className="flex min-w-0 flex-col">
            <span className="text-neutral-primary truncate text-sm font-medium">
              {selectedBuilding.id}
            </span>
            {selectedBuilding.company?.name && (
              <span className="text-neutral-secondary-mid truncate text-xs">
                {selectedBuilding.company.name}
              </span>
            )}
          </div>
        ) : (
          <span className="text-neutral-secondary-mid text-sm">
            Selecteer gebouw
          </span>
        )}
        {!isDisabled && !isLoading && (
          <div className="shrink-0">
            {isOpen ? (
              <ChevronUp width={18} height={18} />
            ) : (
              <ChevronDown width={18} height={18} />
            )}
          </div>
        )}
      </button>

      {isOpen && !isDisabled && !isLoading && (
        <div className="border-neutral-primary/20 absolute top-full right-0 left-0 z-1000 mt-1 max-h-64 overflow-hidden rounded-lg border bg-white shadow-lg">
          <div className="border-neutral-primary/10 border-b p-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Zoek gebouw..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-neutral-primary/20 focus:border-moss-primary w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden"
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => handleBuildingSelect(null)}
              className="hover:bg-moss-primary/10 text-neutral-secondary-mid w-full px-3 py-2 text-left text-sm"
            >
              Geen gebouw selecteren
            </button>
            {filteredBuildings.map((building) => (
              <button
                key={building.id}
                type="button"
                onClick={() => handleBuildingSelect(building)}
                className="hover:bg-moss-primary/10 flex w-full flex-col gap-1 px-3 py-2 text-left"
              >
                <span className="text-neutral-primary text-sm font-medium">
                  {building.id}
                </span>
                {building.company?.name && (
                  <span className="text-neutral-secondary-mid text-xs">
                    {building.company.name}
                  </span>
                )}
              </button>
            ))}

            {filteredBuildings.length === 0 && (
              <div className="text-neutral-secondary-mid px-3 py-2 text-sm">
                Geen gebouwen gevonden
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UnitDropdown: React.FC<UnitDropdownProps> = ({
  selectedUnit,
  selectedBuildingId,
  onUnitSelect,
  onBuildingAutoSelect,
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { units, getUnitsForBuilding, getBuildingFromUnit, isLoading } =
    useBuildingsAndUnits();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const availableUnits = selectedBuildingId
    ? getUnitsForBuilding(selectedBuildingId)
    : units;

  const filteredUnits = availableUnits.filter((unit) =>
    unit.id.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleUnitSelect = (unit: any) => {
    onUnitSelect(unit);

    // Auto-select building if not already selected
    if (!selectedBuildingId && onBuildingAutoSelect) {
      const building = getBuildingFromUnit(unit.id);
      if (building) {
        onBuildingAutoSelect(building.id);
      }
    }

    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isDisabled && !isLoading && setIsOpen(!isOpen)}
        disabled={isDisabled || isLoading}
        className={`flex min-h-[40px] w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
          isDisabled || isLoading
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
            : "border-neutral-primary/20 hover:border-moss-primary focus:border-moss-primary bg-white"
        }`}
      >
        {isLoading ? (
          <span className="text-neutral-secondary-mid text-sm">Laden...</span>
        ) : selectedUnit ? (
          <div className="flex min-w-0 flex-col">
            <span className="text-neutral-primary truncate text-sm font-medium">
              {selectedUnit.id}
            </span>
            {selectedUnit.unitType && (
              <span className="text-neutral-secondary-mid truncate text-xs">
                {selectedUnit.unitType}
              </span>
            )}
          </div>
        ) : (
          <span className="text-neutral-secondary-mid text-sm">
            Selecteer unit (optioneel)
          </span>
        )}
        {!isDisabled && !isLoading && (
          <div className="shrink-0">
            {isOpen ? (
              <ChevronUp width={18} height={18} />
            ) : (
              <ChevronDown width={18} height={18} />
            )}
          </div>
        )}
      </button>

      {isOpen && !isDisabled && !isLoading && (
        <div className="border-neutral-primary/20 absolute top-full right-0 left-0 z-1000 mt-1 max-h-64 overflow-hidden rounded-lg border bg-white shadow-lg">
          <div className="border-neutral-primary/10 border-b p-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Zoek unit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-neutral-primary/20 focus:border-moss-primary w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden"
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => handleUnitSelect(null)}
              className="hover:bg-moss-primary/10 text-neutral-secondary-mid w-full px-3 py-2 text-left text-sm"
            >
              Geen unit selecteren
            </button>
            {filteredUnits.map((unit) => (
              <button
                key={unit.id}
                type="button"
                onClick={() => handleUnitSelect(unit)}
                className="hover:bg-moss-primary/10 flex w-full flex-col gap-1 px-3 py-2 text-left"
              >
                <span className="text-neutral-primary text-sm font-medium">
                  {unit.id}
                </span>
                {unit.unitType && (
                  <span className="text-neutral-secondary-mid text-xs">
                    {unit.unitType}
                  </span>
                )}
              </button>
            ))}

            {filteredUnits.length === 0 && (
              <div className="text-neutral-secondary-mid px-3 py-2 text-sm">
                {selectedBuildingId
                  ? "Geen units gevonden voor dit gebouw"
                  : "Geen units gevonden"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LabelDropdown: React.FC<LabelDropdownProps> = ({
  selectedLabel,
  onLabelSelect,
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const allLabels = Object.values(TicketLabel);
  const filtered = allLabels.filter((l) =>
    l.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleSelect = (label: TicketLabel | null) => {
    onLabelSelect(label);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`flex min-h-[40px] w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
          isDisabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
            : "border-neutral-primary/20 hover:border-moss-primary focus:border-moss-primary bg-white"
        }`}
      >
        {selectedLabel ? (
          <span className="text-neutral-primary truncate text-sm font-medium">
            {selectedLabel}
          </span>
        ) : (
          <span className="text-neutral-secondary-mid text-sm">
            Selecteer label
          </span>
        )}
        {!isDisabled && (
          <div className="shrink-0">
            {isOpen ? (
              <ChevronUp width={18} height={18} />
            ) : (
              <ChevronDown width={18} height={18} />
            )}
          </div>
        )}
      </button>

      {isOpen && !isDisabled && (
        <div className="border-neutral-primary/20 absolute top-full right-0 left-0 z-1000 mt-1 max-h-64 overflow-hidden rounded-lg border bg-white shadow-lg">
          <div className="border-neutral-primary/10 border-b p-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Zoek label..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-neutral-primary/20 focus:border-moss-primary w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="hover:bg-moss-primary/10 text-neutral-secondary-mid w-full px-3 py-2 text-left text-sm"
            >
              Geen label selecteren
            </button>
            {filtered.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => handleSelect(label as TicketLabel)}
                className="hover:bg-moss-primary/10 w-full px-3 py-2 text-left text-sm"
              >
                {label}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="text-neutral-secondary-mid px-3 py-2 text-sm">
                Geen labels gevonden
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const QuickAddTicketModal: React.FC<QuickAddTicketModalProps> = ({
  isOpen,
  onClose,
  onTicketAdded,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { session } = useSession();
  const { users: activeUsers } = useActiveUsers();
  const [selectedAssignee, setSelectedAssignee] = useState<any>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [createInsurance, setCreateInsurance] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<TicketLabel | null>(null);
  const navigate = useNavigate();

  // Focus title input when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isSubmitting) {
      setTitle("");
      setDescription("");
      setSelectedBuilding(null);
      setSelectedUnit(null);
      onClose();
    }
  };

  const handleBuildingSelect = (building: any) => {
    setSelectedBuilding(building);
    // Clear unit if it doesn't belong to the selected building
    if (selectedUnit && building && selectedUnit.building?.id !== building.id) {
      setSelectedUnit(null);
    }
  };

  const handleUnitSelect = (unit: any) => {
    setSelectedUnit(unit);
  };

  const handleBuildingAutoSelect = () => {
    // This will be called when a unit is selected and we need to auto-select its building
    // For simplicity, we'll find the building from our buildings list
    // In a real implementation, you might want to fetch this from the unit data
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Titel is verplicht");
      titleInputRef.current?.focus();
      return;
    }
    if (!selectedBuilding) {
      toast.error("Gebouw is verplicht");
      return;
    }
    if (!selectedLabel) {
      toast.error("Label is verplicht");
      return;
    }

    setIsSubmitting(true);
    try {
      const ticketData = {
        title: title.trim(),
        description: description.trim() || title.trim(),
        priority: TicketPriority.UNKNOWN,
        internalStatus: TicketInternalStatus.NEW_UNANSWERED,
        label: selectedLabel || TicketLabel.UNKNOWN,
        source: "Intern",
        ...(selectedAssignee && { assignedTo: selectedAssignee.id }),
        ...(selectedBuilding && { building: { id: selectedBuilding.id } }),
        ...(selectedUnit && { unit: { id: selectedUnit.id } }),
      };

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ticketData),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to create ticket");
      }

      const savedTicket = await response.json();
      toast.success(`Ticket H${savedTicket.id} succesvol aangemaakt!`);

      // Optionally create insurance issue
      if (createInsurance) {
        try {
          const insuranceBody: any = {
            title: savedTicket.title,
            description: savedTicket.description || savedTicket.title,
            buildingId: selectedBuilding?.id,
            unitId: selectedUnit?.id,
            helpdeskTicket: { id: savedTicket.id },
          };
          const insRes = await fetchWithAuth(
            `${API_URL}/insurance/file`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(insuranceBody),
            },
            session,
          );
          if (insRes.ok) {
            const issue = await insRes.json();
            // Link back to ticket to ensure consistency
            try {
              await fetchWithAuth(
                `${API_URL}/helpdesk/ticket/${savedTicket.id}`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ insuranceIssue: { id: issue.id } }),
                },
                session,
              );
            } catch {}
            toast.success(`Verzekeringsdossier #${issue.id} aangemaakt`);
          } else {
            toast.error("Verzekeringsdossier aanmaken mislukt");
          }
        } catch (e) {
          console.error(e);
          toast.error("Verzekeringsdossier aanmaken mislukt");
        }
      }

      // Reset form and close modal
      handleClose();
      // Navigate to helpdesk and refresh
      navigate("/helpdesk");
      onTicketAdded?.();
    } catch (error) {
      console.error("Failed to create ticket:", error);
      toast.error("Error bij aanmaken van ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && !isSubmitting) {
      handleClose();
    } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-w-2xl",
        backdrop: "bg-black/50",
        wrapper: "items-center justify-center",
      }}
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 pb-2">
          <Lightning size={24} className="text-moss-primary" />
          <span className="text-neutral-primary text-xl font-semibold">
            Snel ticket toevoegen
          </span>
        </ModalHeader>

        <ModalBody className="pb-6">
          <div className="space-y-4">
            <Input
              ref={titleInputRef}
              label="Titel"
              placeholder="Korte beschrijving van het probleem..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="bordered"
              classNames={{
                input: "text-neutral-primary",
                inputWrapper:
                  "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary",
              }}
              isDisabled={isSubmitting}
              isRequired
            />

            <Textarea
              label="Beschrijving (optioneel)"
              placeholder="Aanvullende informatie over het probleem..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="bordered"
              minRows={3}
              maxRows={6}
              classNames={{
                input: "text-neutral-primary",
                inputWrapper:
                  "border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary",
              }}
              isDisabled={isSubmitting}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-neutral-primary mb-2 block text-sm font-medium">
                  <Buildings size={16} className="mr-1 inline" />
                  Gebouw <span className="text-red-600">*</span>
                </label>
                <BuildingDropdown
                  selectedBuilding={selectedBuilding}
                  onBuildingSelect={handleBuildingSelect}
                  isDisabled={isSubmitting}
                />
                {!selectedBuilding && !isSubmitting && (
                  <p className="mt-1 text-xs text-red-600">
                    Gebouw is verplicht
                  </p>
                )}
              </div>

              <div>
                <label className="text-neutral-primary mb-2 block text-sm font-medium">
                  <StackSimple size={16} className="mr-1 inline" />
                  Unit
                </label>
                <UnitDropdown
                  selectedUnit={selectedUnit}
                  selectedBuildingId={selectedBuilding?.id}
                  onUnitSelect={handleUnitSelect}
                  onBuildingAutoSelect={handleBuildingAutoSelect}
                  isDisabled={isSubmitting}
                />
              </div>
            </div>

            {/* Label selection */}
            <div>
              <label className="text-neutral-primary mb-2 block text-sm font-medium">
                Label <span className="text-red-600">*</span>
              </label>
              <LabelDropdown
                selectedLabel={selectedLabel}
                onLabelSelect={setSelectedLabel}
                isDisabled={isSubmitting}
              />
              {!selectedLabel && !isSubmitting && (
                <p className="mt-1 text-xs text-red-600">Label is verplicht</p>
              )}
            </div>

            {/* Toggle to create insurance issue */}
            <div className="mt-2 flex items-center justify-between">
              <span className="text-neutral-primary text-sm">
                Verzekeringsdossier aanmaken
              </span>
              <Switch
                size="sm"
                isSelected={createInsurance}
                onValueChange={setCreateInsurance}
              />
            </div>

            {/* Quick assignee selection */}
            <div>
              <label className="text-neutral-primary mb-2 block text-sm font-medium">
                Toegewezen
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {activeUsers?.map((u: any) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setSelectedAssignee(u)}
                    className={`flex items-center gap-2 rounded-md border p-2 text-left transition ${
                      selectedAssignee?.id === u.id
                        ? "border-moss-primary bg-moss-primary/10"
                        : "border-neutral-primary/20 hover:border-moss-primary"
                    }`}
                  >
                    <img
                      src={u.image_url}
                      alt={`${u.first_name} ${u.last_name}`}
                      className="h-7 w-7 rounded-full object-cover"
                    />
                    <span className="text-neutral-primary truncate text-sm">
                      {u.first_name} {u.last_name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-neutral-primary/10 flex items-center justify-between border-t pt-4">
              <div className="text-neutral-secondary-mid text-xs">
                💡 Tip: Druk Ctrl+Enter om snel op te slaan
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="light"
                  onClick={handleClose}
                  isDisabled={isSubmitting}
                  startContent={<X size={16} />}
                >
                  Annuleren
                </Button>
                <Button
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  className="bg-moss-primary hover:bg-moss-primary/90 text-white"
                  startContent={!isSubmitting ? <Check size={16} /> : undefined}
                >
                  {isSubmitting ? "Ticket aanmaken..." : "Ticket aanmaken"}
                </Button>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
